// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import type { InfiniteData } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import { Filter, Loader2, Search, UnfoldHorizontal, X } from "lucide-react";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import {
  ActiveFiltersDisplay,
  type FilterColumn,
} from "@/components/active-filters-display";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import type { FilterValue } from "@/components/filter-popover";
import { PersonsColumnSelector } from "@/components/persons-column-selector";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSavedViewContextOptional } from "@/contexts/saved-view-context";
import { usePersonDetail } from "@/hooks/api/use-person-detail";
import {
  type Person,
  type PersonsResponse,
  useInfinitePersons,
  usePersonsExport,
} from "@/hooks/api/use-persons";
import { useAvailableFilterFields } from "@/hooks/use-available-filter-fields";
import {
  type SelectableColumn,
  usePersonColumnSelection,
} from "@/hooks/use-column-selection";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { usePersonCustomFieldSelection } from "@/hooks/use-custom-field-selection";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { usePersonFilterState } from "@/hooks/use-person-filter-state";
import { usePersonListFilters } from "@/hooks/use-person-list-filters";
import { useSavedViewState } from "@/hooks/use-saved-view-state";
import { useSearchState } from "@/hooks/use-search-state";
import { getStageVariant } from "@/lib/badge-utils";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDate, formatDateForFilename } from "@/lib/date-formatting";
import {
  type FilterClearingInfo,
  processMetricCardFilters,
} from "@/lib/filter-display-utils";
import type { QueryParams } from "@/lib/process-query-params";
import { cn } from "@/lib/utils";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getPersonColumnDefs, getPersonColumns } from "./person-columns";

interface PersonDetailsSheetProps {
  person: Person | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function PersonDetailsSheet({
  person,
  isOpen,
  onOpenChange,
}: PersonDetailsSheetProps) {
  const { data: personDetail, isLoading: isLoadingDetail } = usePersonDetail(
    person?.id,
  );

  if (!isOpen) return null;

  const renderPersonDetails = () => {
    if (!person) {
      return (
        <div className="flex items-center justify-center h-full">
          <Skeleton className="h-32 w-32" />
        </div>
      );
    }

    const displayPerson = personDetail || person;

    return (
      <div className="space-y-4">
        <div className="mb-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/persons/${person.id}/explorer`, "_blank");
            }}
          >
            Open in Person Explorer
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground">
            <span className="font-medium min-w-[4.5rem]">Email:</span>
            <span>{displayPerson.email_address || "-"}</span>
          </div>

          <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground">
            <span className="font-medium min-w-[4.5rem]">Job Title:</span>
            <span>{displayPerson.job_title || "-"}</span>
          </div>

          <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground">
            <span className="font-medium min-w-[4.5rem]">Employee:</span>
            <span>{displayPerson.is_employee ? "Yes" : "No"}</span>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-bold mb-4">Summary</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted/30 p-4 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">
                Opportunities
              </div>
              <div className="text-3xl font-bold">
                {displayPerson.opportunity_count || 0}
              </div>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">
                Touchpoints
              </div>
              <div className="text-3xl font-bold">
                {displayPerson.touchpoint_count || 0}
              </div>
            </div>
          </div>
        </div>

        {isLoadingDetail ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              Associated Accounts
            </h4>
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          personDetail?.accounts &&
          personDetail.accounts.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Associated Accounts
                <span className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">
                  {personDetail.accounts.length}
                </span>
              </h4>
              <div className="divide-y rounded-md border">
                {personDetail.accounts.map((account) => (
                  <div
                    key={account.id}
                    className="py-3 px-3 flex items-start gap-3 hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-1 rounded-sm bg-supporting-blue/80 self-stretch" />

                    <div className="min-w-0 flex-1">
                      <div className="text-base leading-snug">
                        <a
                          href={`/accounts/${account.id}/explorer`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground hover:underline truncate"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {account.name || account.source_record_id || "-"}
                        </a>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {account.type && (
                          <Badge variant="category-account" className="text-xs">
                            {account.type}
                          </Badge>
                        )}
                        {account.record_type_name && (
                          <Badge variant="default" className="text-xs">
                            {account.record_type_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        {isLoadingDetail ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              Associated Opportunities
            </h4>
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          personDetail?.opportunities &&
          personDetail.opportunities.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Associated Opportunities
                <span className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">
                  {personDetail.opportunities.length}
                </span>
              </h4>
              <div className="divide-y rounded-md border">
                {personDetail.opportunities.map((opp) => {
                  const isClosed = opp.is_closed;
                  const isWon = opp.is_won;
                  const railClass = isClosed
                    ? isWon
                      ? "bg-supporting-green/85"
                      : "bg-supporting-red/85"
                    : "bg-supporting-blue/85";

                  return (
                    <div
                      key={opp.id}
                      className="py-3 px-3 flex items-start gap-3 hover:bg-muted/30 transition-colors"
                    >
                      {/* Hierarchy cue rail (status color) */}
                      <div
                        className={`w-1 rounded-sm ${railClass} self-stretch`}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="text-base leading-snug">
                          <a
                            href={`/accounts/${opp.account_id}/explorer?opportunityId=${opp.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground hover:underline truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {opp.name}
                          </a>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {opp.stage_name && (
                            <Badge
                              variant={getStageVariant(isClosed, isWon)}
                              className="text-xs"
                            >
                              {opp.stage_name}
                            </Badge>
                          )}
                          {opp.type && (
                            <Badge
                              variant="opportunity-type"
                              className="text-xs"
                            >
                              {opp.type}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          <span>
                            Created:{" "}
                            {opp.created_date
                              ? formatDate(opp.created_date as string)
                              : "-"}
                          </span>
                          <span className="mx-2">•</span>
                          <span>
                            Close:{" "}
                            {opp.close_date
                              ? formatDate(opp.close_date as string)
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  const personName = person
    ? [person.first_name, person.last_name].filter(Boolean).join(" ") ||
      person.email_address ||
      "Person Details"
    : "Person Details";

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl bg-card">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-2xl">{personName}</SheetTitle>
          </div>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-6">
          {renderPersonDetails()}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

interface PersonsDataTableProps {
  /**
   * Optional data source to use instead of the default useInfinitePersons.
   */
  dataSource?: {
    data: InfiniteData<PersonsResponse> | undefined;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  /**
   * Optional callback when a row is clicked.
   * If provided, this will be called instead of opening the default details sheet.
   */
  onRowClick?: (person: Person) => void;
  /**
   * Optional complete state override for future integrations.
   */
  stateOverride?: {
    columns: {
      columnVisibility: Record<string, boolean>;
      selectedCustomColumns: SelectableColumn[];
      customFieldIdsParam: string;
      availableColumns: SelectableColumn[];
      selectedColumns: SelectableColumn[];
      selectedCoreColumns: SelectableColumn[];
      setColumnVisibility: (visibility: Record<string, boolean>) => void;
      toggleColumn: (columnId: string) => void;
      isColumnSelected: (columnId: string) => boolean;
      clearSelection: () => void;
      resetToDefault: () => void;
      getVisibleColumnDefs: <T>(columnDefs: ColumnDef<T>[]) => ColumnDef<T>[];
      isLoading?: boolean;
      error?: Error | null;
    };
    filters: {
      activeFilters: Record<string, unknown>;
      updateFilter: (key: string, value: unknown) => void;
      clearFilter: (key: string) => void;
      clearAllFilters: () => void;
    };
    getQueryParams: (options: {
      searchTerm?: string;
      columns?: unknown[];
      limit?: number;
    }) => Record<string, unknown>;
  };
  onFiltersChange?: (filters: Record<string, unknown>) => void;
  rightToolbarActions?: React.ReactNode;
  onExportCSV?: () => Promise<Record<string, unknown>[]>;
}

export function PersonsDataTable({
  dataSource,
  onRowClick: customOnRowClick,
  stateOverride,
  onFiltersChange,
  rightToolbarActions,
  onExportCSV,
}: PersonsDataTableProps = {}) {
  const PERSON_METRIC_LIMIT = 20000;

  const { state, isMobile } = useSidebar();
  const savedViewContext = useSavedViewContextOptional();
  const isLoadingView = savedViewContext?.isLoadingView ?? false;
  const loadedViewId = savedViewContext?.loadedViewId;
  const { resetSpecificKeys } = useSavedViewState({
    pageType: "persons::list",
  });

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "opportunity_count",
    direction: "desc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("persons::list::search");

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    resetToDefaultPinned,
    dndState: { sensors, handleDragEnd },
    getInitializeColumnOrder,
  } = usePersistedColumnState("persons::list::columns", {
    getDefaultPinnedColumns: () => ["name"],
  });

  // Relation filters state
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    clearAllFilters: clearRelationFilters,
  } = usePersonFilterState("persons::list");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Get field definitions for relation filter display
  const { fields: accountFieldDefs = [], isLoading: isLoadingAccountFields } =
    useAvailableFilterFields("account");
  const {
    fields: opportunityFieldDefs = [],
    isLoading: isLoadingOpportunityFields,
  } = useAvailableFilterFields("opportunity");
  const {
    fields: touchpointFieldDefs = [],
    isLoading: isLoadingTouchpointFields,
  } = useAvailableFilterFields("touchpoint");

  // Process relation filters for display
  // Only process if we have filters and field definitions are loaded
  const relationFiltersProcessed = useMemo(() => {
    // Don't process until field definitions are loaded
    if (
      (hasAccountFilters && isLoadingAccountFields) ||
      (hasOpportunityFilters && isLoadingOpportunityFields) ||
      (hasTouchpointFilters && isLoadingTouchpointFields)
    ) {
      return { displayFilters: {}, filterColumns: [], totalFilterCount: 0 };
    }

    return processMetricCardFilters(
      accountFilters,
      opportunityFilters,
      touchpointFilters,
      hasAccountFilters,
      hasOpportunityFilters,
      hasTouchpointFilters,
      accountFieldDefs,
      opportunityFieldDefs,
      touchpointFieldDefs,
    );
  }, [
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    accountFieldDefs,
    opportunityFieldDefs,
    touchpointFieldDefs,
    isLoadingAccountFields,
    isLoadingOpportunityFields,
    isLoadingTouchpointFields,
  ]);

  const relationDisplayFilters = relationFiltersProcessed.displayFilters;
  const relationFilterColumns = relationFiltersProcessed.filterColumns;

  // Handler to clear individual relation filters
  const handleClearRelationFilter = useCallback(
    (_filterKey: string, clearingInfo?: FilterClearingInfo) => {
      if (clearingInfo?.type === "individual") {
        const filterType = clearingInfo.filterGroupType;
        const filterIndex = clearingInfo.index;

        if (filterType === "account") {
          const newFilters = accountFilters.filters.filter(
            (_filter, idx: number) => idx !== filterIndex,
          );
          setAccountFilters({
            ...accountFilters,
            filters: newFilters,
          });
        } else if (filterType === "opportunity") {
          const newFilters = opportunityFilters.filters.filter(
            (_filter, idx: number) => idx !== filterIndex,
          );
          setOpportunityFilters({
            ...opportunityFilters,
            filters: newFilters,
          });
        } else if (filterType === "touchpoint") {
          const newFilters = touchpointFilters.filters.filter(
            (_filter, idx: number) => idx !== filterIndex,
          );
          setTouchpointFilters({
            ...touchpointFilters,
            filters: newFilters,
          });
        }
      }
    },
    [
      accountFilters,
      opportunityFilters,
      touchpointFilters,
      setAccountFilters,
      setOpportunityFilters,
      setTouchpointFilters,
    ],
  );

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { ref: loadMoreRef, inView } = useInView();

  // Always initialize hook; ignore when stateOverride provided
  const internalCustomFieldSelection = usePersonCustomFieldSelection();
  const fieldIdsParam = stateOverride
    ? stateOverride.columns.customFieldIdsParam
    : internalCustomFieldSelection?.fieldIdsParam || "";

  // Normalize to CustomFieldDefinition[] regardless of override/internal path
  const selectedCustomFieldDefs = useMemo<CustomFieldDefinition[]>(() => {
    if (stateOverride) {
      const cols = stateOverride.columns.selectedCustomColumns || [];
      const defs = cols
        .map((c) => c.customField)
        .filter(Boolean) as CustomFieldDefinition[];
      const map = new Map<string, CustomFieldDefinition>();
      defs.forEach((d) => map.set(d.id, d));
      return Array.from(map.values());
    }
    return internalCustomFieldSelection?.selectedFieldsList || [];
  }, [stateOverride, internalCustomFieldSelection?.selectedFieldsList]);

  // Use person list filters hook
  const internalFilterState = usePersonListFilters();

  // Use stateOverride filters if provided, otherwise use internal state
  const filters =
    stateOverride?.filters.activeFilters || internalFilterState.filters;
  const getDefaultSortSettings = internalFilterState.getDefaultSortSettings;
  const getQueryParams =
    stateOverride?.getQueryParams || internalFilterState.getQueryParams;
  const hasFilters = Object.keys(filters).length > 0;

  // Wrap filter functions to call onFiltersChange when provided
  const updateFilter = useCallback(
    (key: string, value: unknown) => {
      if (stateOverride) {
        stateOverride.filters.updateFilter(key, value);
      } else {
        internalFilterState.updateFilter(key, value as FilterValue);
        if (onFiltersChange) {
          const newFilters = { ...filters, [key]: value };
          onFiltersChange(newFilters);
        }
      }
    },
    [stateOverride, internalFilterState, filters, onFiltersChange],
  );

  const clearFilter = useCallback(
    (key: string) => {
      if (stateOverride) {
        stateOverride.filters.clearFilter(key);
      } else {
        internalFilterState.clearFilter(key);
        if (onFiltersChange) {
          const newFilters = { ...filters };
          delete newFilters[key];
          onFiltersChange(newFilters);
        }
      }
    },
    [stateOverride, internalFilterState, filters, onFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    if (stateOverride) {
      stateOverride.filters.clearAllFilters();
    } else {
      internalFilterState.clearAllFilters();
      if (onFiltersChange) {
        onFiltersChange({});
      }
    }
  }, [stateOverride, internalFilterState, onFiltersChange]);

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  // Handle clear all filters and reset sort
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    resetSortToDefaults();
    clearSearch();
  }, [clearAllFilters, resetSortToDefaults, clearSearch]);

  // When component mounts, set sort from filters
  useEffect(() => {
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as SortDirection,
      });
    } else {
      const defaults = getDefaultSortSettings();
      setSortConfig({
        key: defaults.sortColumn,
        direction: defaults.sortDirection,
      });
    }
  }, [filters.sortColumn, filters.sortDirection, getDefaultSortSettings]);

  // Define columns, including custom fields if selected
  const columns = useMemo<UnifiedTableColumn<Person>[]>(
    () => getPersonColumns(undefined, selectedCustomFieldDefs),
    [selectedCustomFieldDefs],
  );

  // Always initialize hook; ignore when stateOverride provided
  const internalColumnState = usePersonColumnSelection(columns);

  // Use ALL state from override OR internal (never mixed)
  const columnState = stateOverride?.columns ?? internalColumnState;
  const getVisibleColumnDefs = columnState.getVisibleColumnDefs;
  const columnVisibility = columnState.columnVisibility;
  const setColumnVisibility = columnState.setColumnVisibility;
  const newCustomFieldIdsParam = columnState.customFieldIdsParam;
  const selectedCustomColumns = columnState.selectedCustomColumns;
  const resetColumnsToDefault = columnState.resetToDefault;

  const prevActiveColumnsRef = useRef<string[]>([]);

  const activeColumnIds = useMemo(() => {
    const visibleCore = Object.entries(columnVisibility)
      .filter(([, v]) => v)
      .map(([id]) => id);

    const customIds = selectedCustomColumns.map((c) => c.id);

    return Array.from(new Set([...visibleCore, ...customIds]));
  }, [columnVisibility, selectedCustomColumns]);

  const columnTitleLookupRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const lookup = columnTitleLookupRef.current;
    columns.forEach((col) => {
      const key = col.key.toString();
      if (!lookup[key]) {
        lookup[key] = col.title;
      }
    });
  }, [columns]);

  useEffect(() => {
    const prev = prevActiveColumnsRef.current;
    const removedIds = prev.filter((id) => !activeColumnIds.includes(id));

    if (removedIds.length > 0) {
      removedIds.forEach((id) => {
        if (filters[id] !== undefined) {
          clearFilter(id);
        }

        // Only reset sort if we're not currently loading a saved view
        if (sortConfig.key === id && !isLoadingView) {
          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");

          const columnTitle = columnTitleLookupRef.current[id];
          if (columnTitle) {
            toast.warning("Table sort reset", {
              description: `Sort on ${columnTitle} has been removed because the column was deselected.`,
              duration: 8000,
            });
          }
        }
      });
    }

    prevActiveColumnsRef.current = activeColumnIds;
  }, [
    activeColumnIds,
    filters,
    sortConfig,
    clearFilter,
    resetSortToDefaults,
    isLoadingView,
  ]);

  // Memoize deduplication of custom fields to avoid recreating on every render
  const uniqueCustomFields = useMemo(() => {
    return [
      ...selectedCustomFieldDefs,
      ...selectedCustomColumns
        .filter((column) => column.customField)
        .map((column) => column.customField as CustomFieldDefinition),
    ].reduce((map, field) => {
      map.set(field.id, field);
      return map;
    }, new Map<string, CustomFieldDefinition>());
  }, [selectedCustomFieldDefs, selectedCustomColumns]);

  const columnDefs = useMemo<ColumnDef<Person>[]>(() => {
    const allDefs = getPersonColumnDefs(
      undefined,
      Array.from(uniqueCustomFields.values()),
    );

    return getVisibleColumnDefs(
      allDefs.map((col) => ({ ...col, enableResizing: true })),
    );
  }, [uniqueCustomFields, getVisibleColumnDefs]);

  // Get query parameters for API call with deduplicated field IDs
  const queryParams = useMemo(() => {
    const combinedCustomFieldIds = [
      ...(fieldIdsParam ? fieldIdsParam.split(",") : []),
      ...(newCustomFieldIdsParam ? newCustomFieldIdsParam.split(",") : []),
    ]
      .filter(Boolean)
      .filter((id, index, array) => array.indexOf(id) === index)
      .join(",");

    const baseParams: Record<string, unknown> = {
      ...getQueryParams({
        searchTerm: debouncedSearchQuery,
        columns,
        limit: 200,
      }),
      extra_fields: combinedCustomFieldIds,
    };

    const relationFilters: Record<string, unknown> = {};
    if (hasAccountFilters) {
      relationFilters.account_filters = accountFilters;
    }
    if (hasOpportunityFilters) {
      relationFilters.opportunity_filters = opportunityFilters;
    }
    if (hasTouchpointFilters) {
      relationFilters.touchpoint_filters = touchpointFilters;
    }

    if (Object.keys(relationFilters).length > 0) {
      baseParams.relation_filters = JSON.stringify(relationFilters);
    }

    return baseParams as QueryParams;
  }, [
    getQueryParams,
    debouncedSearchQuery,
    columns,
    fieldIdsParam,
    newCustomFieldIdsParam,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    accountFilters,
    opportunityFilters,
    touchpointFilters,
  ]);

  // Reset ONLY column widths
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Reset column order to default while preserving pinned columns at the start
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [columnDefs, setColumnOrder, pinnedColumns]);

  // Reset pinned columns - respects saved views if loaded
  const resetPinnedColumns = useCallback(() => {
    if (loadedViewId) {
      // Reset to saved view's configuration
      resetSpecificKeys([
        "persons::list::columns::pinned",
        "persons::list::columns::pinned-original-positions",
      ]);
    } else {
      // No saved view - reset to default pinned state
      resetToDefaultPinned();
    }
  }, [loadedViewId, resetSpecificKeys, resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to defaults
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });

    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Reset relation filters
  const handleResetRelationFilters = useCallback(() => {
    clearRelationFilters();
  }, [clearRelationFilters]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    handleClearAllFilters();
    resetColumnsToDefault();
    resetPinnedColumns();
    resetColumnOrder();
    resetColumnWidths();
    resetWrappedColumns();
    clearRelationFilters();
  }, [
    handleClearAllFilters,
    resetColumnsToDefault,
    resetColumnOrder,
    resetColumnWidths,
    resetPinnedColumns,
    resetWrappedColumns,
    clearRelationFilters,
  ]);

  // Use provided data source or fetch persons with infinite scrolling
  const defaultDataSource = useInfinitePersons(
    dataSource ? undefined : queryParams,
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = dataSource || defaultDataSource;

  // Filter columns for CSV export based on visibility
  const visibleColumnsForExport = useMemo(() => {
    return columns.filter((col) => {
      const colId = col.key.toString();

      // Check if it's a custom field
      if (col.isCustomField) {
        return selectedCustomColumns.some((c) => c.id === colId);
      }

      // For required columns, always include them (matching table display behavior)
      // This ensures consistency with the table which always shows required columns
      if (col.required === true) {
        return true;
      }

      // For columns with defaultHidden: true, only include if explicitly visible
      if (col.defaultHidden === true) {
        return columnVisibility[colId] === true;
      }

      // For other core columns, check visibility state
      // Include if not explicitly hidden (handles uninitialized state)
      return columnVisibility[colId] !== false;
    });
  }, [columns, columnVisibility, selectedCustomColumns]);

  // Setup for CSV export
  const { exportPersonsAsync } = usePersonsExport();

  // Set up paginated CSV export with custom fields
  const exportAllData = useMemo<() => Promise<Person[]>>(() => {
    if (onExportCSV) {
      return async () => {
        const rows = await onExportCSV();
        return rows as unknown as Person[];
      };
    }
    return () => exportPersonsAsync(queryParams);
  }, [onExportCSV, exportPersonsAsync, queryParams]);

  const { exportData, isExporting, defaultFilename } =
    usePaginatedCSVExport<Person>({
      fetchAllData: exportAllData,
      columns: visibleColumnsForExport,
      filename: `persons_${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Flatten the pages of data into a single array with memoization
  const persons = useMemo(() => {
    if (!data || !data.pages || data.pages.length === 0) return [];
    if (data.pages.length === 1) return data.pages[0].results;
    return data.pages.flatMap((page) => page.results) || [];
  }, [data]);

  // Total count of persons
  const totalCount = data?.pages[0]?.total || 0;

  // Check if the metric limit is exceeded
  const isMetricLimitExceeded = totalCount > PERSON_METRIC_LIMIT;

  // Helper function for metric limit tooltip
  const getMetricLimitTooltipText = useCallback(() => {
    return `Apply additional filters to sort on metrics. For performance reasons, sorting on metrics is currently disabled when more than ${PERSON_METRIC_LIMIT} persons match filters.`;
  }, []);

  // Function to display the sort reset toast notification
  const showSortResetToast = useCallback((columnTitle: string) => {
    const description = `For performance reasons, the sort on ${columnTitle} has been removed. Please filter to fewer than ${PERSON_METRIC_LIMIT} persons and then try re-applying the sort.`;

    toast.warning("Table sort reset", {
      description,
      duration: 10000,
    });
  }, []);

  // Track when a metric sort warning appears and reset sort (API driven)
  useEffect(() => {
    const firstPage = data?.pages[0];
    const touchpointMetricColumns = new Set([
      "touchpoint_count",
      "first_touchpoint_date",
      "last_touchpoint_date",
    ]);

    if (firstPage?.client_warning !== "SORT_ON_METRIC_TOO_MANY_MATCHING") {
      return;
    }

    // Check if we're sorting on a metric column that would trigger the warning
    if (!touchpointMetricColumns.has(sortConfig.key)) {
      return;
    }

    // Capture the column name before resetting
    const columnToReset = sortConfig.key;
    const column = columns.find((col) => col.key === columnToReset);

    // Reset sort to default
    setSortConfig({ key: "opportunity_count", direction: "desc" });
    clearFilter("sortColumn");
    clearFilter("sortDirection");

    // Show informative toast
    if (column?.title) {
      showSortResetToast(column.title);
    }
    // Note: We intentionally don't include sortConfig in dependencies
    // to avoid re-triggering when we update it above.
    // The guard (touchpointMetricColumns.has) ensures we only reset once.
  }, [data?.pages, clearFilter, columns, showSortResetToast, sortConfig.key]);

  // Column auto-sizing function
  const handleDoubleClickResize = useCallback(
    (_: MouseEvent, columnId: string) => {
      if (isLoading) return;

      requestAnimationFrame(() => {
        try {
          const resizeHandler = createColumnResizeHandler(
            setColumnSizing,
            "persons-table",
            { sampleSize: 30 },
          );

          resizeHandler(_, columnId);
        } catch (e) {
          console.error("Column auto-sizing failed:", e);
          setColumnSizing((prev) => ({ ...prev, [columnId]: 150 }));
        }
      });
    },
    [isLoading, setColumnSizing],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useCallback(
    (columnId: string) => {
      if (isLoading) return;
      requestAnimationFrame(() => {
        try {
          const resizeHandler = createSingleColumnResizeHandler(
            setColumnSizing,
            "persons-table",
            { sampleSize: 30 },
          );
          resizeHandler(columnId);
        } catch (e) {
          console.error("Column auto-sizing failed:", e);
          setColumnSizing((prev) => ({ ...prev, [columnId]: 150 }));
        }
      });
    },
    [isLoading, setColumnSizing],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useCallback(
    (columnIds: string[]) => {
      if (isLoading) return;
      requestAnimationFrame(() => {
        try {
          const resizeHandler = createBulkColumnResizeHandler(
            setColumnSizing,
            "persons-table",
            { sampleSize: 30 },
          );
          resizeHandler(columnIds);
        } catch (e) {
          console.error("Bulk column auto-sizing failed:", e);
        }
      });
    },
    [isLoading, setColumnSizing],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      const next: Record<string, boolean> = {
        ...(columnVisibility as Record<string, boolean>),
        [columnId]: false,
      };
      setColumnVisibility(next);
    },
    [setColumnVisibility, columnVisibility],
  );

  // Initialize TanStack Table
  const table = useReactTable({
    data: persons,
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
      columnVisibility,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
  });

  const rowModel = table.getRowModel();
  const hasWrappedColumns = wrappedColumns.length > 0;
  const rowVirtualizer = useVirtualizer({
    count: rowModel.rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: hasWrappedColumns ? () => 60 : () => 36,
    overscan: 10,
    measureElement: hasWrappedColumns
      ? (el) => el?.getBoundingClientRect().height ?? 60
      : undefined,
  });

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // Load more data when the load more element is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle sort
  const handleSort = useCallback(
    (columnKey: string) => {
      const column = columns.find((col) => col.key === columnKey);
      if (!column) return;

      // Block metric sorting when limit exceeded
      const touchpointMetricColumns = new Set([
        "touchpoint_count",
        "first_touchpoint_date",
        "last_touchpoint_date",
      ]);
      const isMetricColumn = touchpointMetricColumns.has(columnKey);
      if (isMetricColumn && isMetricLimitExceeded) return;

      const newDirection =
        sortConfig.key === columnKey
          ? sortConfig.direction === "asc"
            ? "desc"
            : "asc"
          : column.defaultSortDirection || "asc";

      setSortConfig({ key: columnKey, direction: newDirection });
      updateFilter("sortColumn", columnKey);
      updateFilter("sortDirection", newDirection);
    },
    [columns, sortConfig, updateFilter, isMetricLimitExceeded],
  );

  // Handle row click to open sidesheet
  const handleRowClick = useCallback(
    (personId: string) => {
      if (customOnRowClick) {
        const person = persons.find((p) => p.id === personId);
        if (person) {
          customOnRowClick(person);
        }
      } else {
        if (selectedPerson !== personId) {
          setSelectedPerson(personId);
          setIsSheetOpen(true);
        }
      }
    },
    [selectedPerson, customOnRowClick, persons],
  );

  // Render sort icon
  const renderSortIcon = useCallback(
    (column: UnifiedTableColumn<Person>) => {
      if (!column.sortable) return null;

      // Hide sort icon for metric columns when limit exceeded
      const key = column.key.toString();
      const touchpointMetricColumns = new Set([
        "touchpoint_count",
        "first_touchpoint_date",
        "last_touchpoint_date",
      ]);
      const isMetricColumn = touchpointMetricColumns.has(key);
      if (isMetricColumn && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === column.key}
          direction={
            sortConfig.key === column.key ? sortConfig.direction : null
          }
          showOnHover={false}
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // Render loading state
  const renderLoadingState = () => {
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {headerGroups[0].headers.map((header) => (
                <TableCell key={`${i}-${header.id}`}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Memoize pinnedColumnPositions calculation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render table content
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          handleDoubleClickResize={handleDoubleClickResize}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          onRemoveColumn={handleRemoveColumn}
        />
        <TableBody className="bg-card relative" style={{ height: totalSize }}>
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading persons:{" "}
                {error instanceof Error ? error.message : String(error)}
              </TableCell>
            </TableRow>
          ) : persons.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No persons found.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {virtualRows.map((virtualRow: VirtualItem) => {
                const row = rowModel.rows[virtualRow.index];
                return (
                  <TableRow
                    ref={
                      rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
                    }
                    key={row.id}
                    data-index={virtualRow.index}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      display: "flex",
                    }}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleRowClick(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isPinned = pinnedColumns.includes(cell.column.id);
                      const isWrapped = wrappedColumns.includes(cell.column.id);
                      const pinnedLeft = pinnedColumnPositions[cell.column.id];

                      return (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                            ...(isPinned && { left: `${pinnedLeft}px` }),
                          }}
                          className={cn(
                            cell.column.id === sortConfig.key && "bg-muted/40",
                            isPinned && "pinned-column bg-background",
                          )}
                          data-column-id={cell.column.id}
                        >
                          <div
                            className={cn(
                              "cell-content",
                              isWrapped && "wrapped",
                            )}
                            title={String(row.getValue(cell.column.id) || "")}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {/* Load more trigger element */}
              <TableRow ref={loadMoreRef}>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className={cn("h-12", !hasNextPage && "hidden")}
                >
                  {!isFetchingNextPage && hasNextPage ? "Load more" : null}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </>
    );
  };

  return (
    <>
      <Card className="h-full relative flex flex-col">
        {/* Fixed position loading indicator */}
        {isFetchingNextPage && (
          <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-background/90 shadow-md rounded-md px-4 py-2 border">
            <div className="flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="ml-2">Loading more...</span>
            </div>
          </div>
        )}

        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filter by name or email"
                className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
                onChange={(e) => handleSearchChange(e.target.value)}
                value={searchInputValue}
              />
              {searchInputValue && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
                </Button>
              )}
            </div>
            <InfoTooltip
              trigger={
                <button
                  className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Wildcard search info"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircledIcon className="h-3.5 w-3.5" />
                </button>
              }
              side="right"
              sideOffset={5}
            >
              <div className="text-sm space-y-2">
                <p className="font-medium">Supports wildcards</p>
                <ul className="space-y-1">
                  <li>
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">
                      _
                    </code>{" "}
                    matches a single character
                  </li>
                  <li>
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">
                      %
                    </code>{" "}
                    matches zero or more characters
                  </li>
                </ul>
              </div>
            </InfoTooltip>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {isLoading
                ? "Loading..."
                : totalCount > 0
                  ? `${totalCount.toLocaleString()} matching persons`
                  : "No matching persons"}
            </div>
            {rightToolbarActions}

            <PersonsColumnSelector
              columnState={
                stateOverride
                  ? {
                      availableColumns: columnState.availableColumns,
                      isColumnSelected: columnState.isColumnSelected,
                      toggleColumn: columnState.toggleColumn,
                      isLoading: columnState.isLoading,
                      error: columnState.error,
                    }
                  : undefined
              }
            />

            {/* Relation Filters Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
              data-testid="relation-filters-button"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasAccountFilters ||
                  hasOpportunityFilters ||
                  hasTouchpointFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Relation Filters</span>
              {(hasAccountFilters ||
                hasOpportunityFilters ||
                hasTouchpointFilters) && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {(accountFilters?.filters?.length || 0) +
                    (opportunityFilters?.filters?.length || 0) +
                    (touchpointFilters?.filters?.length || 0)}
                </Badge>
              )}
            </Button>

            {/* Autosize All Columns Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const visibleColumnIds = columnOrder.filter(
                      (columnId) =>
                        (columnVisibility as Record<string, boolean>)[
                          columnId
                        ] !== false,
                    );
                    handleBulkColumnResize(visibleColumnIds);
                  }}
                  className="h-8 w-8"
                  disabled={isLoading}
                >
                  <UnfoldHorizontal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Resize all visible columns to fit their content</p>
              </TooltipContent>
            </Tooltip>

            <ResetTableMenu
              onResetWidths={resetColumnWidths}
              onResetOrder={resetColumnOrder}
              onResetColumns={resetColumnsToDefault}
              onResetTableFilters={handleClearAllFilters}
              onResetPinnedColumns={resetPinnedColumns}
              onResetSort={resetSortOrder}
              onResetWrappedColumns={resetWrappedColumns}
              onResetRelationFilters={handleResetRelationFilters}
              onResetAll={handleResetTable}
            />

            {/* CSV Export Button */}
            <ExportFilenameDialog
              open={isExportDialogOpen}
              onOpenChange={setIsExportDialogOpen}
              defaultFilename={defaultFilename}
              extension="csv"
              onExport={handleExport}
              disabled={isExporting || persons.length === 0}
            >
              <CSVExportButton
                onClick={() => setIsExportDialogOpen(true)}
                isExporting={isExporting}
                disabled={isExporting || persons.length === 0}
                tooltipText="Export persons data to CSV"
              />
            </ExportFilenameDialog>
          </div>
        </div>

        <div className="px-4 pb-2 space-y-2 flex-shrink-0">
          {/* Table-level filters */}
          {hasFilters && (
            <div className="flex items-center justify-between">
              <ActiveFiltersDisplay
                filters={filters}
                columns={columns as FilterColumn[]}
                clearFilter={clearFilter}
                clearAllFilters={handleClearAllFilters}
                clearAllText="Clear all table filters"
                filterType="table"
              />
            </div>
          )}

          {/* Relation filters */}
          {Object.keys(relationDisplayFilters).length > 0 && (
            <div className="flex items-center justify-between">
              <ActiveFiltersDisplay
                filters={relationDisplayFilters}
                columns={relationFilterColumns}
                clearFilter={handleClearRelationFilter}
                clearAllFilters={clearRelationFilters}
                clearAllText="Clear all relation filters"
                filterType="relation"
              />
            </div>
          )}
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={constrainedClosestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToTableBounds]}
        >
          <div
            ref={tableContainerRef}
            className={cn(
              "rounded-md border overflow-y-auto flex-grow",
              table.getState().columnSizingInfo.isResizingColumn &&
                "table-resizing",
            )}
            style={{
              maxWidth: isMobile
                ? "calc(100vw - 2rem)"
                : state === "collapsed"
                  ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                  : "calc(100vw - var(--sidebar-width) - 2rem)",
              minWidth: "640px",
            }}
          >
            <Table
              className="w-full table-fixed"
              style={{ width: table.getTotalSize() }}
              data-table-id="persons-table"
              data-table-container
            >
              {isLoading ? renderLoadingState() : renderTableContent()}
            </Table>
          </div>
        </DndContext>
      </Card>

      <PersonDetailsSheet
        person={
          selectedPerson
            ? persons.find((p) => p.id === selectedPerson) || null
            : null
        }
        isOpen={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) {
            setSelectedPerson(null);
          }
        }}
      />

      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="persons::list"
        entity="person"
      />
    </>
  );
}
